import { Select, SelectOptionType } from '@bp/ui-components';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiValue } from 'react-select';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { SubstitutionDataEntry } from '../../../pages/Substitutions/Plan/PlanSubstitutions/utils';
import { use_EventsInRangeQuery, use_SchoolEventsQuery } from '../../../types/planung-graphql-client-defs';
import { isFirstBetweenSecondAndThird } from '../../../utils/dateCalculations';
import { useSubstitutionStore } from '../SubstitutionProvider';
import styles from './SubstitutionRoomsSelect.module.scss';

type SubstitutionRoomsSelectProps = {
  originalRoomUuids: string[];
  eventRoomUuids: string[];
  eventStart: Dayjs;
  eventEnd: Dayjs;
  error: string | null;
  onChange: (uuids: string[]) => void;
  disabled?: boolean;
  // ohne Label und Fehlerzeile, das Label wird dann als Placeholder angezeigt
  dense?: boolean;
};

export const SubstitutionRoomsSelect = ({
  originalRoomUuids,
  eventRoomUuids,
  eventStart,
  eventEnd,
  error,
  onChange,
  disabled,
  dense,
}: SubstitutionRoomsSelectProps) => {
  const { t } = useTranslation();
  const eventContext = useMemorizedCacheTag('EVENT');

  const { selectedDay } = useSubstitutionStore();
  const selectedDayStartString = dayjs(selectedDay).startOf('day').utc().toISOString();
  const selectedDayEndString = dayjs(selectedDay).endOf('day').utc().toISOString();

  const { roomsData } = useLoadBasicData({ pause: false });

  const [{ data: eventData }] = use_EventsInRangeQuery({
    variables: { start: selectedDayStartString, end: selectedDayEndString, includeSchoolEvents: true },
    context: eventContext,
  });

  const availableRooms: SubstitutionDataEntry[] = useMemo(() => {
    const rooms: SubstitutionDataEntry[] = [];

    // find rooms that are taken at the same time
    const sameTimeRoomUuids =
      eventData?.eventsInRange
        .filter((event) => isFirstBetweenSecondAndThird(event.start, eventStart, eventEnd, 'minutes'))
        .flatMap((event) => event.roomUuids) ?? [];

    // get all rooms
    const allRoomUuids = roomsData.rooms.filter((room) => room.classroom).map((room) => room.uuid);
    // substract taken rooms
    const availableRoomUuids = allRoomUuids.filter((uuid) => !sameTimeRoomUuids.includes(uuid));
    // push rooms from current event
    originalRoomUuids.forEach((uuid) => availableRoomUuids.push(uuid));

    // map available rooms
    availableRoomUuids.forEach((uuid) => {
      const room = roomsData.rooms.find((room) => room.uuid === uuid);
      if (room) {
        rooms.push({
          uuid: room.uuid,
          name: room.name,
          shortName: room.roomNumber ?? '',
        });
      }
    });

    return rooms.sort((a, b) => {
      return a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    });
  }, [eventData?.eventsInRange, roomsData.rooms, originalRoomUuids, eventStart, eventEnd]);

  const roomOptions = useCreateSelectOptions(availableRooms, 'uuid', 'name');

  return (
    <Select
      disabled={disabled}
      label={t('substitutions.availableRooms')}
      placeholder={dense ? t('substitutions.availableRooms') : undefined}
      dense={dense}
      options={roomOptions}
      onChange={(option) => {
        const values = option as MultiValue<SelectOptionType>;
        onChange(values.map((v) => v.value as string));
      }}
      value={roomsData.rooms
        .filter((ro) => eventRoomUuids?.includes(ro.uuid))
        .map((ro) => ({ value: ro.uuid, label: ro.name }))}
      isMulti
      isClearable
      isSearchable
      name='available-rooms'
      menuPosition='fixed'
      error={error ?? undefined}
      className={styles['substitution-rooms-select']}
    />
  );
};
